import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { TextareaFormField } from 'src/shared/components/ui/forms/form-fields/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/shared/components/ui/popups/dialog'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import { useCreateSupportTicket } from 'src/shared/features/support/api/use-create-support-ticket'
import { useSendSentryBug } from 'src/shared/features/support/api/use-send-sentry-bug'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useToast } from 'src/shared/hooks/use-toast'
import type { SupportTicketCreate, SupportType } from 'src/shared/schemas/forms/support'
import { supportTicketCreateSchema, supportTypeMap } from 'src/shared/schemas/forms/support'
import { cn } from 'src/shared/utils/cn'

interface SupportDialogV2Props {
  open: boolean
  setOpen: (open: boolean) => void
}

const SENTRY_REPORT_TYPES: SupportType[] = [
  'account_issues',
  'referral_issues',
  'general_feedback',
  'technical_support',
]

export default function SupportDialogV2({ open, setOpen }: SupportDialogV2Props) {
  const { isMobile } = useBreakpoint()
  const { currentAuth } = useAuthState()
  const createTicket = useCreateSupportTicket()
  const sendBug = useSendSentryBug()
  const isLoading = createTicket.isPending || sendBug.isPending
  const { toast } = useToast()

  const form = useForm<SupportTicketCreate>({
    resolver: zodResolver(supportTicketCreateSchema),
    defaultValues: {
      type: 'technical_support',
      email: currentAuth?.email || undefined,
      description: '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset()
    }
  }, [open])

  const handleSubmit = async (data: SupportTicketCreate) => {
    if (isLoading) return

    try {
      await createTicket.mutateAsync(data)

      if (SENTRY_REPORT_TYPES.includes(data.type)) {
        await sendBug.mutateAsync({
          name: currentAuth?.username || undefined,
          email: currentAuth?.email || undefined,
          message: `[${data.type}] ${data.description}`,
        })
      }
      toast({
        variant: 'default',
        title: 'Thank you for submitting your request!',
        description:
          'Our team will review it and get in touch with you shortly. We appreciate your patience and enthusiasm as we continue to improve Conrad!',
      })
      setOpen(false)
      form.reset()
    } catch (error) {
      console.error(error)
    }
  }

  // Hack to get the error messages to show. TODO: Find a better solution.
  try {
    // Do nothing
  } finally {
    // Do nothing
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className={cn(
          'flex max-w-[550px] flex-col gap-0 overflow-hidden rounded-[25px] p-0 sm:rounded-[25px]',
          isMobile && 'max-w-full rounded-none',
        )}
        closeIconClassName="size-6 top-6 right-6"
        fullScreen={isMobile}
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-[25px] py-[80px] xs:px-[45px] xs:py-[39px]">
          <DialogHeader className="mb-[30px]">
            <DialogTitle className="text-left text-[26px] font-semibold leading-tight tracking-[-0.32px] xs:text-[32px]">
              Support request
            </DialogTitle>
            <p className="mt-[10px] text-left text-[16px] leading-[1.6] text-muted xs:text-[18px]">
              Got feedback or issues? We&apos;re here to help!
            </p>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-3">
              <SelectFormField
                control={form.control}
                name="type"
                label="Type"
                options={supportTypeMap}
                variant="new"
                triggerClassName="pl-5"
                labelClassName="px-5"
                valueClassName="ml-0"
              />
              {!currentAuth?.email && (
                <InputFormField
                  control={form.control}
                  name="email"
                  label="Email"
                  className={cn('space-y-1')}
                  fieldClassName={cn(
                    'focus-visible:outline-none text-primary border-none text-[16px] translate-y-[22px] placeholder:translate-y-0 bg-transparent px-5',
                  )}
                  floatingLabelClassName="peer-focus:top-2 peer-focus:text-muted peer-placeholder-shown:text-sm pointer-events-none bg-transparent -translate-y-[1px] px-1 peer-focus:px-1 peer-focus:-translate-y-[1px] peer-placeholder-shown:translate-y-[-42%]"
                  fieldContainerClassName="bg-accent rounded-[12px] h-[60px]"
                  type={'email'}
                  useNewErrorMessageComponent={true}
                />
              )}
              <TextareaFormField
                control={form.control}
                name="description"
                label=""
                minRows={6}
                maxRows={8}
                description="Tell us what's happening..."
                className="rounded-[12px] border-none bg-accent"
                fieldClassName="bg-accent border-none rounded-[12px] p-5"
              />
            </form>
          </Form>
        </div>

        <div className="border-secondaryBorder flex items-center justify-end border-t px-[25px] pb-[38px] pt-[17px] xs:py-[15px]">
          <Button
            type="submit"
            form={undefined}
            onClick={form.handleSubmit(handleSubmit)}
            className="h-[50px] w-full min-w-[180px] rounded-full text-[15px] font-semibold xs:w-auto"
            isLoading={isLoading}
          >
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
